const DICOM_STORE_QUERY_KEYS = [
  'dicomStore',
  'dicomStorePath',
  'gcpDicomStorePath',
  'dicomwebRoot',
  'dicomWebRoot',
] as const;

const GCP_DICOM_STORE_PATH_PATTERN =
  /projects\/[^/]+\/locations\/[^/]+\/datasets\/[^/]+\/dicomStores\/[^/?#]+/i;

const DICOM_STORE_TILDE_PREFIX = 'dicomStore~';

type DicomwebRoots = {
  qidoRoot?: string;
  wadoRoot?: string;
  wadoUriRoot?: string;
};

function getParamCaseInsensitive(sp: URLSearchParams, key: string): string | null {
  if (sp.has(key)) {
    return sp.get(key);
  }

  const target = key.toLowerCase();
  for (const [k, v] of sp.entries()) {
    if (k.toLowerCase() === target) {
      return v;
    }
  }

  return null;
}

export function getGcpDicomStorePathFromDicomwebRoot(dicomwebRoot?: string): string | null {
  const raw = String(dicomwebRoot || '').trim();
  if (!raw) {
    return null;
  }

  const match = raw.match(GCP_DICOM_STORE_PATH_PATTERN);
  return match?.[0] || null;
}

export function getDicomStorePathFromConfig(config?: DicomwebRoots | null): string | null {
  const root = config?.qidoRoot || config?.wadoRoot || config?.wadoUriRoot;
  return getGcpDicomStorePathFromDicomwebRoot(root);
}

/**
 * Parse the compact tilde format: dicomStore~projects~myProject~locations~...
 */
export function parseDicomStoreTildeParam(search: string): string | null {
  const match = String(search || '').match(/(?:^|[?&])dicomStore~([^&#]*)/i);
  if (!match?.[1]) {
    return null;
  }

  const path = match[1]
    .split('~')
    .map(segment => decodeURIComponent(segment).trim())
    .filter(Boolean)
    .join('/');

  return path || null;
}

/**
 * Read dicomStore from either legacy `dicomStore=projects/...` or compact `dicomStore~projects~...`.
 */
export function getDicomStorePathFromSearch(
  search: string,
  searchParams: URLSearchParams = new URLSearchParams(search)
): string | null {
  const fromStandard =
    getParamCaseInsensitive(searchParams, 'dicomStore') ||
    getParamCaseInsensitive(searchParams, 'dicomStorePath') ||
    getParamCaseInsensitive(searchParams, 'gcpDicomStorePath');

  if (fromStandard) {
    return String(fromStandard).trim() || null;
  }

  return parseDicomStoreTildeParam(search);
}

export function formatDicomStoreTildeParam(storePath: string): string {
  const trimmed = String(storePath || '').trim().replace(/^\//, '');
  const match = trimmed.match(GCP_DICOM_STORE_PATH_PATTERN);
  const path = match?.[0] || trimmed;
  const segments = path.split('/').filter(Boolean);
  return `${DICOM_STORE_TILDE_PREFIX}${segments.join('~')}`;
}

function stripDicomStoreParams(search: string): string {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) {
    return '';
  }

  return raw
    .split('&')
    .filter(part => {
      if (!part) {
        return false;
      }

      const eqIdx = part.indexOf('=');
      const key = eqIdx === -1 ? part : part.slice(0, eqIdx);
      const decodedKey = decodeURIComponent(key);
      const keyLower = decodedKey.toLowerCase();

      if (keyLower.startsWith(DICOM_STORE_TILDE_PREFIX.toLowerCase())) {
        return false;
      }

      return !DICOM_STORE_QUERY_KEYS.some(k => keyLower === k.toLowerCase());
    })
    .join('&');
}

export function applyDicomStoreToHref(href: string, storePath: string | null): string {
  const url = new URL(href);
  const remaining = stripDicomStoreParams(url.search);
  const tildeParam = storePath ? formatDicomStoreTildeParam(storePath) : null;
  url.search = [remaining, tildeParam].filter(Boolean).join('&');
  return url.toString();
}

export function setDicomStoreInBrowserUrl(storePath: string | null): void {
  try {
    window.history.replaceState({}, '', applyDicomStoreToHref(window.location.href, storePath));
  } catch {
    // Never block the viewer on URL updates.
  }
}

export function hasDicomStoreQueryParam(
  params: URLSearchParams,
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): boolean {
  if (getDicomStorePathFromSearch(search, params)) {
    return true;
  }

  for (const key of DICOM_STORE_QUERY_KEYS) {
    if (key === 'dicomwebRoot' || key === 'dicomWebRoot') {
      continue;
    }

    if (params.has(key) && params.get(key)) {
      return true;
    }

    const target = key.toLowerCase();
    for (const [k, v] of params.entries()) {
      if (k.toLowerCase() === target && v) {
        return true;
      }
    }
  }

  const dicomwebRoot =
    getParamCaseInsensitive(params, 'dicomwebRoot') ||
    getParamCaseInsensitive(params, 'dicomWebRoot');
  if (dicomwebRoot) {
    return true;
  }

  return !!parseDicomStoreTildeParam(search);
}

function resolveStorePathForShareableLink(
  currentSearch: string,
  dataSourceConfig?: DicomwebRoots | null
): string | null {
  const params = new URLSearchParams(currentSearch);
  return getDicomStorePathFromSearch(currentSearch, params) || getDicomStorePathFromConfig(dataSourceConfig);
}

export function appendDicomStoreFromConfig(
  query: URLSearchParams,
  config?: DicomwebRoots | null
): void {
  if (hasDicomStoreQueryParam(query)) {
    return;
  }

  const storePath = getDicomStorePathFromConfig(config);
  if (storePath) {
    query.set('dicomStore', storePath);
  }
}

export function finalizeShareableQueryString(
  query: URLSearchParams,
  dataSourceConfig?: DicomwebRoots | null,
  currentSearch: string = typeof window !== 'undefined' ? window.location.search : ''
): string {
  for (const key of DICOM_STORE_QUERY_KEYS) {
    query.delete(key);
  }

  const base = query.toString();
  const storePath = resolveStorePathForShareableLink(currentSearch, dataSourceConfig);

  if (!storePath) {
    return base;
  }

  const tildeParam = formatDicomStoreTildeParam(storePath);
  return base ? `${base}&${tildeParam}` : tildeParam;
}

export function appendDicomStoreTildeToExistingQuery(
  queryString: string,
  dataSourceConfig?: DicomwebRoots | null,
  currentSearch: string = typeof window !== 'undefined' ? window.location.search : ''
): string {
  const cleaned = stripDicomStoreParams(queryString);
  const storePath = resolveStorePathForShareableLink(currentSearch, dataSourceConfig);

  if (!storePath) {
    return cleaned;
  }

  const tildeParam = formatDicomStoreTildeParam(storePath);
  return cleaned ? `${cleaned}&${tildeParam}` : tildeParam;
}

/**
 * Ensure the browser address bar includes dicomStore for shareable viewer links.
 */
export function syncDicomStoreInBrowserUrl(config?: DicomwebRoots | null): void {
  try {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const existingPath = getDicomStorePathFromSearch(search, params);

    if (existingPath) {
      if (!parseDicomStoreTildeParam(search)) {
        setDicomStoreInBrowserUrl(existingPath);
      }
      return;
    }

    if (hasDicomStoreQueryParam(params, search)) {
      return;
    }

    const storePath = getDicomStorePathFromConfig(config);
    if (!storePath) {
      return;
    }

    setDicomStoreInBrowserUrl(storePath);
  } catch {
    // Never block the viewer on URL updates.
  }
}
